import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";

export default function AuditLog() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = useCallback(() => {
    api.get("/admin/audit-logs", { params: { page, page_size: pageSize } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div data-testid="audit-log-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Audit Log</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A permanent record of every administrative action. Audit entries cannot be edited or deleted.
        </p>
      </div>
      {!data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : data.items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="audit-empty">
          <p className="font-heading text-lg font-bold text-navy">No activity recorded yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-border bg-secondary/60 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Date / Time</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">User</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Module</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Action</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((a) => (
                  <tr key={a.id} className="hover:bg-secondary/30" data-testid={`audit-row-${a.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-foreground/80">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-foreground/80">{a.user_email}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-navy/70">{a.module}</span>
                    </td>
                    <td className="px-4 py-3 capitalize text-foreground/80">{a.action}</td>
                    <td className="max-w-md px-4 py-3 text-foreground/80">{a.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between" data-testid="audit-pagination">
            <p className="text-xs text-muted-foreground">Page {data.page} of {totalPages} · {data.total} entries</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="audit-next-page">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
